import type {
  PolycardContractContext,
  PolycardContractInterface,
} from '@polycard/card';
import type { Metrics } from '../../../../utils/contracts';
import type { DeviceType, OsNames } from '../../../utils/utils';

import { useEffect } from 'react';

import { useEmpty } from '../../../hooks/useEmpty';
import { usePagination } from '../../../providers/Pagination';
import { getCardType } from '../../../utils/utils';
import { GenericPolycard } from '../../GenericPolycard';
import PaginationList from '../../PaginationList/paginationList';
import { ProductListComponent } from '../../ProductList';
import { Snackbar } from '../../Snackbar';
import { DenounceProfile } from '../../DenunceProfile';
import { useWordings } from '../../../hooks/useWordings';
import { BreadcrumbList } from '../../BreadcrumbList/BreadcrumbList';
import { useAppContext } from '../../../providers/AppProvider';

interface ListDetailProps {
  isOwner: boolean;
  deviceType: DeviceType;
  listId: string;
  metrics: Metrics;
  polycardContext?: PolycardContractContext;
  polycards?: Array<PolycardContractInterface>;
  shareLink: string;
  osName: OsNames;
  domain: string;
  listOwnerId: string;
  listName: string;
  urlLists: string;
  page: string;
}

export interface BreadcrumbLevelList {
  text: string;
  title?: string;
  id: string;
  disabled: boolean;
  onClick?: any;
}

export const ListDetail = ({
  deviceType,
  isOwner,
  listId,
  metrics,
  osName,
  shareLink,
  polycardContext,
  polycards,
  domain,
  listOwnerId,
  listName,
  urlLists,
  page,
}: ListDetailProps) => {
  const { trackingId } = useAppContext();
  const { FOOTER_TEXT, FOOTER_LINK, MY_LISTS, MY_RECOMMENDATIONS } =
    useWordings();
  const { emptyComponent, showEmpty, emptyComponentDefault } = useEmpty({
    isOwner,
    isListDetail: true,
    urlLists,
  });
  const { totalItems: paginationTotalItems, setTotalItems } = usePagination();
  const footerUrl = `${domain}/noindex/denounce?bu=mercado_libre&product=affiliates&affiliate_user_id=${listOwnerId}`;

  useEffect(() => {
    showEmpty(paginationTotalItems === 0);
  }, [paginationTotalItems, showEmpty, setTotalItems]);

  const handleRedirect = () => {
    const hash = trackingId ? `#tracking_id=${trackingId}` : '';
    window.location.href = `${urlLists}${hash}`;
  };

  const levels: Array<BreadcrumbLevelList> = [
    {
      text: MY_LISTS,
      title: MY_LISTS,
      id: '1',
      disabled: false,
      onClick: handleRedirect,
    },
    {
      text: listName === 'DEFAULT' ? MY_RECOMMENDATIONS : listName,
      id: '2',
      disabled: true,
    },
  ];

  const titleList = (
    <>
      <BreadcrumbList levels={levels} />
      <ProductListComponent
        isOwner={isOwner}
        shareLink={shareLink}
        deviceType={deviceType}
        osName={osName}
        metrics={metrics}
        listName={listName}
      />
    </>
  );

  const showPolycard =
    polycards &&
    polycards.length > 0 &&
    polycardContext &&
    Object.keys(polycardContext).length > 0;

  if (showPolycard) {
    polycardContext.type = getCardType(deviceType);
  } else {
    return (
      <>
        {titleList} {emptyComponentDefault}
      </>
    );
  }

  return (
    <>
      {titleList}
      {showPolycard && (
        <>
          <GenericPolycard
            key="list.id"
            deviceType={deviceType}
            listId={listId}
            metrics={metrics}
            polycardContext={polycardContext}
            polycards={polycards}
            type="grid"
            isOwner={isOwner}
          />
          <PaginationList page={page} />
          <Snackbar />
        </>
      )}
      {emptyComponent}
      {!emptyComponent && (
        <DenounceProfile
          label={FOOTER_TEXT}
          link={FOOTER_LINK}
          url={footerUrl}
          deviceType="desktop"
        />
      )}
    </>
  );
};
